import { useMutation, useQueries, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { api } from './api'
import type { TaskWithState, TodayView, WeekView } from '../../shared/types'

export const keys = {
  bootstrap: ['bootstrap'] as const,
  today: (memberId: string | null) => ['today', memberId] as const,
  week: (memberId: string | null) => ['week', memberId] as const,
  family: ['family'] as const,
  plans: (scope: string) => ['plans', scope] as const,
  connection: (weekKey: string | null) => ['connection', weekKey] as const,
  tasks: ['tasks'] as const,
  members: ['members'] as const,
  approvals: ['approvals'] as const,
  sentBack: ['sent-back'] as const,
}

const undef = (id: string | null): string | undefined => id ?? undefined

export function useToday(memberId: string | null) {
  return useQuery({
    queryKey: keys.today(memberId),
    queryFn: () => api.today(undef(memberId)),
    enabled: memberId !== null,
    // Another device can approve or send back a job, so keep this fresh.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

/** Several members' days at once, sharing cache entries with `useToday`. */
export function useTodays(memberIds: string[]) {
  return useQueries({
    queries: memberIds.map((memberId) => ({
      queryKey: keys.today(memberId),
      queryFn: () => api.today(memberId),
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    })),
  })
}

export function useWeek(memberId: string | null) {
  return useQuery({
    queryKey: keys.week(memberId),
    queryFn: () => api.week(undef(memberId)),
    enabled: memberId !== null,
  })
}

export function useFamily() {
  return useQuery({ queryKey: keys.family, queryFn: api.family, refetchInterval: 120_000 })
}

export function usePlans(scope: 'upcoming' | 'all' = 'upcoming') {
  return useQuery({
    queryKey: keys.plans(scope),
    queryFn: () => api.plans(scope === 'all' ? 'all' : undefined),
  })
}

export function useConnection(weekKey: string | null = null) {
  return useQuery({ queryKey: keys.connection(weekKey), queryFn: () => api.connection(weekKey ?? undefined) })
}

export function useTasks() {
  return useQuery({ queryKey: keys.tasks, queryFn: api.tasks })
}

export function useMembers() {
  return useQuery({ queryKey: keys.members, queryFn: api.members })
}

/**
 * Ticking a chore should feel instant on a phone, so the cached Today and Week
 * views are patched up front and reconciled from the server afterwards.
 */
/** Kid ticks waiting for a parent. Only fetched for parents; kids can't read it. */
export function useApprovals(enabled: boolean) {
  return useQuery({
    queryKey: keys.approvals,
    queryFn: api.approvals,
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

/** Today's send-backs and whether each was redone. Parents only, like approvals. */
export function useSentBack(enabled: boolean) {
  return useQuery({
    queryKey: keys.sentBack,
    queryFn: api.sentBack,
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

/**
 * `byParent` decides how an optimistic tick looks: a parent's tick is the
 * approval, a kid's waits for one. The server applies the same rule.
 */
export function useToggleTask(memberId: string | null, byParent: boolean): UseMutationResult<
  unknown,
  Error,
  { taskId: string; done: boolean; periodKey?: string },
  { previousToday?: TodayView; previousWeek?: WeekView }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ taskId, done, periodKey }: { taskId: string; done: boolean; periodKey?: string }) =>
      done ? api.complete(taskId, undef(memberId)) : api.uncomplete(taskId, undef(memberId), periodKey),

    onMutate: async ({ taskId, done }) => {
      const tick = (t: TaskWithState): TaskWithState =>
        t.id === taskId ? { ...t, done, approved: done && (byParent || t.approved) } : t
      await Promise.all([
        queryClient.cancelQueries({ queryKey: keys.today(memberId) }),
        queryClient.cancelQueries({ queryKey: keys.week(memberId) }),
      ])

      const previousToday = queryClient.getQueryData<TodayView>(keys.today(memberId))
      const previousWeek = queryClient.getQueryData<WeekView>(keys.week(memberId))

      if (previousToday) {
        queryClient.setQueryData<TodayView>(keys.today(memberId), {
          ...previousToday,
          personalDaily: previousToday.personalDaily.map(tick),
          householdDaily: previousToday.householdDaily.map(tick),
        })
      }

      if (previousWeek) {
        queryClient.setQueryData<WeekView>(keys.week(memberId), {
          ...previousWeek,
          personalWeekly: previousWeek.personalWeekly.map(tick),
          householdWeekly: previousWeek.householdWeekly.map(tick),
        })
      }

      return { previousToday, previousWeek }
    },

    onError: (_error, _vars, context) => {
      if (context?.previousToday) queryClient.setQueryData(keys.today(memberId), context.previousToday)
      if (context?.previousWeek) queryClient.setQueryData(keys.week(memberId), context.previousWeek)
    },

    // The server recomputes Ready for Tomorrow, so always
    // settle against it rather than trusting the optimistic patch.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.today(memberId) })
      void queryClient.invalidateQueries({ queryKey: keys.week(memberId) })
      void queryClient.invalidateQueries({ queryKey: keys.family })
      void queryClient.invalidateQueries({ queryKey: keys.approvals })
      void queryClient.invalidateQueries({ queryKey: keys.sentBack })
    },
  })
}

/** Mutation wrapper that refreshes everything a house-rule change can affect. */
export function useRefreshingMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
): UseMutationResult<TResult, Error, TArgs> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries()
    },
  })
}
